import { DownOutlined, RightOutlined, SearchOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Empty,
  Flex,
  Input,
  Modal,
  Slider,
  Space,
  Spin,
  Statistic,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { useEffect, useMemo, useState } from "react";
import MainMetrics from "../../components/MainMetrics";
import RangeAnnotatedChart from "../../components/charts/apexRangeAnnotatedChart";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import useEventImpact from "../../hooks/useEventImpact";
import useEventSkuTimeseries from "../../hooks/useEventSkuTimeseries";
import useEventSkus from "../../hooks/useEventSkus";
import useEventSkusPorCategoria from "../../hooks/useEventSkusPorCategoria";
import useEventTimeseries from "../../hooks/useEventTimeseries";
import { PROMO_KPIS_METRICS, PROMO_TOOLTIPS } from "../../utils/constants";
import {
  COR_DA_BASE,
  COR_DA_VIGENCIA,
  JANELA_MAXIMA_DA_MEDIA_MOVEL,
  JANELA_PADRAO_DA_MEDIA_MOVEL,
  METRICAS_DO_COMPARATIVO,
  STATUS_DO_SKU,
  TAMANHO_DO_LOTE_DE_SKUS,
  comparativoDoSku,
  corDoLift,
  deltaDoValor,
  densificarSerie,
  faixasDasVigencias,
  faixasDeVigencia,
  formatarData,
  formatarMinutos,
  formatarMoeda,
  formatarPct,
  janelaDoGrafico,
  kpisDaCampanha,
  linhaDoAgregado,
  linhaDoSku,
  linhasAgrupadas,
  mensagemDeErro,
  ontemLocal,
  ordenarCategorias,
  recorteDoGraficoDoSku,
  resumoDasJanelas,
  serieMediaMovelParaApex,
  serieParaApex,
  tagDoStatus,
  textoDosDias,
  textoDosRetratos,
  tipoDoDelta,
  valoresDiarios,
} from "./promoHelpers";

const { Text } = Typography;

const TAMANHO_DA_PAGINA_DE_SKUS = 20;
const ATRASO_DA_BUSCA_MS = 300;
const ESTILO_DA_BUSCA = { width: 320 };
const ESTILO_DO_CARD = { width: "85%" };
const ESTILOS_DO_CARD = {
  header: { backgroundColor: "var(--ouran-card-header-bg)" },
};

const ORDENAVEIS = {
  produto: "produto",
  status: "status",
  vendas: "unidades",
  faturamento: "faturamento_vendas",
  margem: "delta_margem",
  estoque_atual: "estoque_atual",
};

const ORDENACAO_PADRAO = { coluna: "margem", direcao: "descend" };

const FILTROS_DE_STATUS = STATUS_DO_SKU.map((valor) => ({
  text: tagDoStatus(valor).label,
  value: valor,
}));

const FaixaDaJanela = ({ rotulo, faixa, cor }) => (
  <Flex vertical gap={2}>
    <Text type="secondary" style={{ fontSize: 12 }}>
      {rotulo}
    </Text>
    <Text strong style={{ fontSize: 18, color: cor, lineHeight: 1.2 }}>
      {faixa.periodo}
    </Text>
    <Text type="secondary" style={{ fontSize: 12 }}>
      {textoDosDias(faixa)}
    </Text>
  </Flex>
);

const formatarQuantidade = (valor) =>
  typeof valor === "number" ? valor.toLocaleString("pt-BR") : "—";

const textoPequeno = { fontSize: 12 };

const TagDeLift = ({ variacao }) => {
  const cor = corDoLift(variacao);
  return cor ? (
    <Tag color={cor}>{formatarPct(variacao, { comSinal: true })}</Tag>
  ) : (
    <Tag>Sem base</Tag>
  );
};

const ValorComDelta = ({ texto, variacao, rotulo, dica }) => {
  const delta = deltaDoValor(texto, variacao);
  const conteudo = (
    <Space direction="vertical" size={0} style={{ alignItems: "flex-end" }}>
      <Text style={{ whiteSpace: "nowrap" }}>
        {texto}
        {delta && (
          <Text type={tipoDoDelta(variacao)} style={textoPequeno}>
            {" "}
            {delta}
          </Text>
        )}
      </Text>
      {rotulo && (
        <Text type="secondary" style={textoPequeno}>
          {rotulo}
        </Text>
      )}
    </Space>
  );
  return dica ? <Tooltip title={dica}>{conteudo}</Tooltip> : conteudo;
};

const COLUNAS_DE_SKU = [
  {
    title: "Produto",
    key: "produto",
    fixed: "left",
    width: 260,
    render: (_, sku) => (
      <Space direction="vertical" size={0} style={{ textAlign: "left" }}>
        <Text code>{sku.produto}</Text>
        <Text>{sku.descricao || "—"}</Text>
        {sku.ean && (
          <Text type="secondary" style={textoPequeno}>
            EAN {sku.ean}
          </Text>
        )}
      </Space>
    ),
  },
  {
    title: <Tooltip title={PROMO_TOOLTIPS.status}>Status</Tooltip>,
    key: "status",
    width: 170,
    render: (_, sku) => {
      const tag = tagDoStatus(sku.status);
      return (
        <Space direction="vertical" size={4}>
          <Tag color={tag.color}>{tag.label}</Tag>
          {sku.retirado_em && (
            <Tag color="volcano">
              Retirado em {formatarData(sku.retirado_em)}
            </Tag>
          )}
        </Space>
      );
    },
  },
  {
    title: "Promoção",
    key: "promocao",
    width: 150,
    render: (_, sku) => (
      <Space direction="vertical" size={0}>
        <Text>
          {typeof sku.valor_promocao === "number"
            ? formatarMoeda(sku.valor_promocao)
            : formatarPct(sku.percentual_desconto)}
        </Text>
        {typeof sku.desconto_implicito_pct === "number" && (
          <Tooltip title={PROMO_TOOLTIPS.desconto_implicito_pct}>
            <Text type="secondary" style={textoPequeno}>
              {formatarPct(sku.desconto_implicito_pct)} implícito
            </Text>
          </Tooltip>
        )}
        {sku.quantidade_minima > 1 && (
          <Text type="secondary" style={textoPequeno}>
            a partir de {sku.quantidade_minima} un.
          </Text>
        )}
      </Space>
    ),
  },
  {
    title: <Tooltip title={PROMO_TOOLTIPS.sku_vendas}>Vendas</Tooltip>,
    key: "vendas",
    align: "right",
    width: 130,
    render: (_, sku) => <ValorComDelta {...linhaDoSku(sku).vendas} />,
  },
  {
    title: (
      <Tooltip title={PROMO_TOOLTIPS.sku_faturamento}>Faturamento</Tooltip>
    ),
    key: "faturamento",
    align: "right",
    width: 170,
    render: (_, sku) => <ValorComDelta {...linhaDoSku(sku).faturamento} />,
  },
  {
    title: <Tooltip title={PROMO_TOOLTIPS.sku_margem}>Margem total</Tooltip>,
    key: "margem",
    align: "right",
    width: 170,
    render: (_, sku) => <ValorComDelta {...linhaDoSku(sku).margem} />,
  },
  {
    title: <Tooltip title={PROMO_TOOLTIPS.sku_atencao}>Atenção</Tooltip>,
    key: "atencao",
    align: "right",
    width: 140,
    render: (_, sku) => <ValorComDelta {...linhaDoSku(sku).atencao} />,
  },
  {
    title: <Tooltip title={PROMO_TOOLTIPS.estoque}>Estoque</Tooltip>,
    dataIndex: "estoque_atual",
    key: "estoque_atual",
    align: "right",
    width: 90,
    render: formatarQuantidade,
  },
];

const FORMATADORES_DO_COMPARATIVO = {
  moeda: formatarMoeda,
  minutos: formatarMinutos,
  quantidade: formatarQuantidade,
};

const textoDosDiasAbertos = (dias) =>
  dias === 1 ? "1 dia aberto" : `${dias} dias abertos`;

const COLUNAS_DO_COMPARATIVO = [
  {
    title: <Tooltip title={PROMO_TOOLTIPS.comparativo}>Período</Tooltip>,
    key: "periodo",
    fixed: "left",
    width: 230,
    render: (_, linha) =>
      linha.key === "variacao" ? (
        <Tooltip title={PROMO_TOOLTIPS.comparativo_variacao}>
          <Text strong>{linha.rotulo}</Text>
        </Tooltip>
      ) : (
        <Space direction="vertical" size={0}>
          <Text>{linha.rotulo}</Text>
          <Text type="secondary" style={textoPequeno}>
            {linha.periodo}
            {linha.dias !== null && ` · ${textoDosDiasAbertos(linha.dias)}`}
          </Text>
        </Space>
      ),
  },
  ...METRICAS_DO_COMPARATIVO.map(({ key, titulo, formato }) => ({
    title: (
      <Tooltip
        title={
          PROMO_TOOLTIPS[`comparativo_${key}`] || PROMO_TOOLTIPS.comparativo
        }>
        {titulo}
      </Tooltip>
    ),
    dataIndex: key,
    key,
    align: "right",
    render: (valor, linha) =>
      linha.key === "variacao" ? (
        <Text strong>{formatarPct(valor, { comSinal: true })}</Text>
      ) : (
        FORMATADORES_DO_COMPARATIVO[formato](valor)
      ),
  })),
];

const TabelaDoComparativo = ({ comparativo }) => (
  <Space direction="vertical" size={4} style={{ width: "100%" }}>
    <Table
      rowKey="key"
      size="small"
      columns={COLUNAS_DO_COMPARATIVO}
      dataSource={comparativo.linhas}
      pagination={false}
      scroll={{ x: 900 }}
    />
    {comparativo.semBase && (
      <Text type="secondary" style={textoPequeno}>
        Sem período anterior com venda na loja para comparar.
      </Text>
    )}
    {comparativo.tamanhosDiferentes && (
      <Text type="secondary" style={textoPequeno}>
        Períodos de tamanhos diferentes: as variações de totais são por média
        diária.
      </Text>
    )}
  </Space>
);

const SERIES_DO_GRAFICO = [
  {
    name: "Faturamento (R$)",
    campo: "faturamento_vendas",
    formatter: "currency",
  },
  {
    name: "Margem (R$)",
    campo: "margem",
    formatter: "currency",
    color: "#8FC7A2",
  },
  { name: "Unidades", campo: "unidades", formatter: "number", hidden: true },
];

// Média móvel: a linha suave leva o valor de cada dia (`diario`) para o tooltip.
// Janela de 1 dia é o diário.
const seriesDoGrafico = (dias, janela) =>
  SERIES_DO_GRAFICO.map(({ campo, ...serie }) => ({
    ...serie,
    data:
      janela > 1
        ? serieMediaMovelParaApex(dias, campo, janela)
        : serieParaApex(dias, campo),
    ...(janela > 1 ? { diario: valoresDiarios(dias, campo) } : {}),
  }));

// Só números: um rótulo largo na ponta ("Diário") invade o texto ao lado.
const MARCAS_DA_MEDIA_MOVEL = {
  1: "1",
  7: "7",
  [JANELA_MAXIMA_DA_MEDIA_MOVEL]: String(JANELA_MAXIMA_DA_MEDIA_MOVEL),
};

const textoDaJanela = (dias) => (dias > 1 ? `${dias} dias` : "Diário");

// Largura fixa para o controle não pular quando o texto muda de tamanho.
const ESTILO_DO_TEXTO_DA_MEDIA = { minWidth: 140, textAlign: "right" };

// O gráfico só redesenha ao soltar o controle, como no Fluxo Diário.
const ControleDaMediaMovel = ({ valor, aoMudar }) => {
  const [arrastando, setArrastando] = useState(valor);
  useEffect(() => setArrastando(valor), [valor]);
  return (
    <Flex align="center" gap={16} style={{ fontWeight: "normal" }}>
      <Text style={ESTILO_DO_TEXTO_DA_MEDIA}>
        {`Média móvel: ${textoDaJanela(arrastando)}`}
      </Text>
      <Slider
        min={1}
        max={JANELA_MAXIMA_DA_MEDIA_MOVEL}
        marks={MARCAS_DA_MEDIA_MOVEL}
        value={arrastando}
        onChange={setArrastando}
        onChangeComplete={aoMudar}
        tooltip={{ formatter: textoDaJanela }}
        style={{ width: 200 }}
        aria-label="Janela da média móvel em dias"
      />
    </Flex>
  );
};

const COLUNAS_COM_AGREGADO = ["vendas", "faturamento", "margem"];

const textoDeSkus = (quantidade) =>
  quantidade === 1 ? "1 SKU" : `${quantidade} SKUs`;

// Total geral: só vendas, faturamento e margem; atenção, estoque e afins
// existem apenas por SKU.
const celulaDoTotal = (chave, totais, linhaDoTotal) => {
  if (chave === "produto") {
    return (
      <Space direction="vertical" size={0}>
        <Text strong>Total geral</Text>
        <Text type="secondary" style={textoPequeno}>
          {textoDeSkus(totais.skus)}
        </Text>
      </Space>
    );
  }
  if (COLUNAS_COM_AGREGADO.includes(chave)) {
    return <ValorComDelta {...linhaDoTotal[chave]} />;
  }
  return null;
};

const renderizarCelulaAgrupada = ({
  coluna,
  valor,
  linha,
  indice,
  aberta,
  aoMostrarMais,
}) => {
  if (linha.tipo === "sku") {
    const conteudo = coluna.render
      ? coluna.render(valor, linha, indice)
      : valor;
    return coluna.key === "produto" ? (
      <div style={{ paddingLeft: 20 }}>{conteudo}</div>
    ) : (
      conteudo
    );
  }
  if (linha.tipo === "categoria") {
    const { categoria } = linha;
    if (coluna.key === "produto") {
      const Seta = aberta ? DownOutlined : RightOutlined;
      return (
        <Flex align="center" gap={8}>
          <Seta style={{ color: "#8c8c8c", fontSize: 11 }} />
          <Space direction="vertical" size={0}>
            <Text strong>{categoria.name}</Text>
            <Text type="secondary" style={textoPequeno}>
              {textoDeSkus(categoria.skus)}
            </Text>
          </Space>
        </Flex>
      );
    }
    if (COLUNAS_COM_AGREGADO.includes(coluna.key)) {
      return <ValorComDelta {...linhaDoAgregado(categoria)[coluna.key]} />;
    }
    return null;
  }
  if (coluna.key !== "produto") return null;
  if (linha.tipo === "carregando") return <Spin size="small" />;
  return (
    <Button
      size="small"
      type="link"
      style={{ paddingLeft: 20 }}
      onClick={(evento) => {
        evento.stopPropagation();
        aoMostrarMais(linha.categoriaKey);
      }}>
      Mostrar mais {TAMANHO_DO_LOTE_DE_SKUS} ({linha.carregados} de{" "}
      {linha.total})
    </Button>
  );
};

export default function EventoPorProdutoDetalhe({
  evento,
  period,
  janela = null,
  baseline,
}) {
  const eventId = evento?.event_id;
  const ontem = useMemo(() => ontemLocal(), []);
  const [status, setStatus] = useState(null);
  const [busca, setBusca] = useState("");
  const [ordenacao, setOrdenacao] = useState(ORDENACAO_PADRAO);
  const [pagina, setPagina] = useState(1);
  const [produtoSelecionado, setProdutoSelecionado] = useState(null);
  const [modalAberto, setModalAberto] = useState(false);
  const [agrupar, setAgrupar] = useState(false);
  const [janelaDaMedia, setJanelaDaMedia] = useState(
    JANELA_PADRAO_DA_MEDIA_MOVEL
  );
  const [abertas, setAbertas] = useState({});
  const buscaAtrasada = useDebouncedValue(busca.trim(), ATRASO_DA_BUSCA_MS);

  useEffect(() => {
    setStatus(null);
    setBusca("");
    setOrdenacao(ORDENACAO_PADRAO);
    setPagina(1);
    setProdutoSelecionado(null);
    setAbertas({});
  }, [eventId, period, janela, baseline]);

  useEffect(() => {
    setPagina(1);
  }, [buscaAtrasada]);

  const impacto = useEventImpact({ eventId, period, janela, baseline });
  const serie = useEventTimeseries({ eventId, period, janela, baseline });
  const campoOrdenado = ordenacao.direcao
    ? (ORDENAVEIS[ordenacao.coluna] ?? null)
    : null;
  const ordemOrdenada = ordenacao.direcao === "ascend" ? "asc" : "desc";

  const skus = useEventSkus({
    eventId,
    period,
    janela,
    baseline,
    status,
    busca: buscaAtrasada,
    ordenarPor: campoOrdenado,
    ordem: ordemOrdenada,
    limit: TAMANHO_DA_PAGINA_DE_SKUS,
    offset: (pagina - 1) * TAMANHO_DA_PAGINA_DE_SKUS,
  });
  const serieDoSku = useEventSkuTimeseries({
    eventId,
    produto: produtoSelecionado,
    period,
    janela,
    baseline,
  });

  const skusPorCategoria = useEventSkusPorCategoria({
    eventId,
    period,
    janela,
    baseline,
    status,
    busca: buscaAtrasada,
    ordenarPor: campoOrdenado,
    ordem: ordemOrdenada,
    abertas,
    enabled: agrupar,
  });

  const listaDeSkus = skus.data?.skus;
  const totais = skus.data?.totais;
  const categorias = useMemo(
    () => ordenarCategorias(skus.data?.categorias, ordenacao),
    [skus.data?.categorias, ordenacao]
  );
  const skusConhecidos = [
    ...(listaDeSkus || []),
    ...Object.values(skusPorCategoria).flatMap((item) => item.skus),
  ];
  const skuSelecionado =
    skusConhecidos.find((sku) => sku.produto === produtoSelecionado) || null;
  const comparativoDoSkuSelecionado = useMemo(
    () => comparativoDoSku(skuSelecionado),
    [skuSelecionado]
  );

  const { dados, indisponiveis } = useMemo(
    () => kpisDaCampanha(impacto.data?.campanha, impacto.data?.entradas),
    [impacto.data]
  );

  const graficoDoEvento = useMemo(() => {
    const resposta = serie.data;
    const janela = janelaDoGrafico(resposta?.janela, resposta?.dias, ontem);
    if (!janela) return null;
    const dias = densificarSerie(resposta.dias, janela.inicio, janela.fim);
    return {
      series: seriesDoGrafico(dias, janelaDaMedia),
      faixas: faixasDeVigencia(resposta.janela, ontem),
    };
  }, [serie.data, ontem, janelaDaMedia]);

  const graficoDoSku = useMemo(() => {
    const resposta = serieDoSku.data;
    if (!resposta) return null;
    // Mesmo eixo do gráfico do evento; promoções antigas do SKU que encostam
    // na janela são recortadas, não esticam o eixo.
    const recorte = recorteDoGraficoDoSku(resposta, serie.data, ontem);
    if (!recorte) return null;
    const { faixas } = recorte;
    const dias = densificarSerie(resposta.dias, recorte.inicio, recorte.fim);
    return {
      titulo: `${resposta.produto} · ${resposta.descricao || ""}`,
      series: seriesDoGrafico(dias, janelaDaMedia),
      faixas,
    };
  }, [serieDoSku.data, serie.data, ontem, janelaDaMedia]);

  const colunasDeSku = useMemo(
    () =>
      COLUNAS_DE_SKU.map((coluna) => {
        const ordenavel = ORDENAVEIS[coluna.key];
        const comOrdem = ordenavel
          ? {
              sorter: true,
              sortOrder:
                ordenacao.coluna === coluna.key ? ordenacao.direcao : null,
            }
          : {};
        const comFiltro =
          coluna.key === "status"
            ? {
                filters: FILTROS_DE_STATUS,
                filterMultiple: false,
                filteredValue: status ? [status] : null,
              }
            : {};
        return { ...coluna, ...comOrdem, ...comFiltro };
      }),
    [ordenacao, status]
  );

  if (!eventId) {
    return null;
  }

  const atencao = impacto.data?.atencao;
  const atencaoMedida = atencao && "variacao_pct" in atencao;

  const aoMudarTabela = (paginacao, filtros, ordenador) => {
    const novoStatus = filtros?.status?.[0] ?? null;
    const coluna = ordenador?.order ? ordenador.columnKey : null;
    const direcao = ordenador?.order ?? null;
    const mudouOEscopo =
      novoStatus !== status ||
      coluna !== ordenacao.coluna ||
      direcao !== ordenacao.direcao;
    setStatus(novoStatus);
    setOrdenacao({ coluna, direcao });
    setPagina(mudouOEscopo ? 1 : (paginacao?.current ?? 1));
  };

  const alternarCategoria = (chave) =>
    setAbertas((atuais) =>
      chave in atuais
        ? Object.fromEntries(
            Object.entries(atuais).filter(([outra]) => outra !== chave)
          )
        : { ...atuais, [chave]: TAMANHO_DO_LOTE_DE_SKUS }
    );

  const mostrarMais = (chave) =>
    setAbertas((atuais) => ({
      ...atuais,
      [chave]: (atuais[chave] || 0) + TAMANHO_DO_LOTE_DE_SKUS,
    }));

  const linhas = agrupar
    ? linhasAgrupadas(categorias, abertas, skusPorCategoria)
    : [];
  const colunasAgrupadas = colunasDeSku.map((coluna) => ({
    ...coluna,
    render: (valor, linha, indice) =>
      renderizarCelulaAgrupada({
        coluna,
        valor,
        linha,
        indice,
        aberta: linha.categoria ? linha.categoria.key in abertas : false,
        aoMostrarMais: mostrarMais,
      }),
  }));
  const linhaDoTotal = linhaDoAgregado(totais);

  const janelas = resumoDasJanelas(
    impacto.data?.dias_antes,
    impacto.data?.dias_depois
  );

  return (
    <>
      {impacto.isError && (
        <Alert
          type="error"
          showIcon
          style={ESTILO_DO_CARD}
          message={mensagemDeErro(impacto.error)}
        />
      )}
      {janelas && (
        <Flex gap={48} wrap="wrap" style={{ width: "85%" }} align="flex-start">
          <FaixaDaJanela
            rotulo="Evento"
            faixa={janelas.evento}
            cor={COR_DA_VIGENCIA}
          />
          <FaixaDaJanela
            rotulo="Comparativo"
            faixa={janelas.comparativo}
            cor={COR_DA_BASE}
          />
          {janelas.tamanhosDiferentes && (
            <Text type="secondary" style={{ alignSelf: "center" }}>
              Janelas de tamanhos diferentes: as variações são por média diária.
            </Text>
          )}
        </Flex>
      )}
      <MainMetrics
        data={dados}
        isLoading={impacto.isLoading}
        kpis={PROMO_KPIS_METRICS}
        unavailableKeys={indisponiveis}
      />
      {atencaoMedida && (
        <Card
          title="Tempo de atenção nas zonas do evento"
          style={ESTILO_DO_CARD}
          styles={ESTILOS_DO_CARD}>
          <Flex gap={48} wrap="wrap" justify="center">
            <Statistic
              title="Média diária antes"
              value={atencao.media_diaria_antes}
              formatter={formatarMinutos}
            />
            <Statistic
              title="Média diária depois"
              value={atencao.media_diaria_depois}
              formatter={formatarMinutos}
            />
            <Statistic
              title="Variação"
              value={atencao.variacao_pct}
              formatter={() => <TagDeLift variacao={atencao.variacao_pct} />}
            />
          </Flex>
        </Card>
      )}
      <Card
        title="Faturamento, margem e unidades dos SKUs do evento"
        extra={
          graficoDoEvento && (
            <ControleDaMediaMovel
              valor={janelaDaMedia}
              aoMudar={setJanelaDaMedia}
            />
          )
        }
        style={ESTILO_DO_CARD}
        styles={ESTILOS_DO_CARD}
        loading={serie.isLoading}>
        {serie.isError ? (
          <Alert type="error" showIcon message={mensagemDeErro(serie.error)} />
        ) : graficoDoEvento ? (
          <RangeAnnotatedChart
            series={graficoDoEvento.series}
            faixas={graficoDoEvento.faixas}
          />
        ) : (
          <Empty />
        )}
      </Card>
      <Card
        title="SKUs da campanha"
        style={ESTILO_DO_CARD}
        styles={ESTILOS_DO_CARD}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <Flex gap={16} wrap="wrap" align="center" justify="space-between">
            <Text type="secondary">
              {textoDosRetratos(
                skus.data?.estoque_ref,
                skus.data?.ultimo_retrato_erp
              )}
            </Text>
            <Flex gap={16} align="center">
              <Space size={8} style={{ whiteSpace: "nowrap" }}>
                <Switch
                  size="small"
                  checked={agrupar}
                  onChange={setAgrupar}
                  aria-label="Agrupar por categoria"
                />
                <Text>Agrupar por categoria</Text>
              </Space>
              <Input
                allowClear
                value={busca}
                onChange={(evento) => setBusca(evento.target.value)}
                placeholder="Buscar por nome, EAN ou SKU"
                prefix={<SearchOutlined />}
                style={ESTILO_DA_BUSCA}
                aria-label="Buscar SKU da campanha"
              />
            </Flex>
          </Flex>
          <Table
            rowKey={agrupar ? "rowKey" : "produto"}
            size="small"
            columns={agrupar ? colunasAgrupadas : colunasDeSku}
            dataSource={agrupar ? linhas : listaDeSkus || []}
            loading={skus.isFetching}
            onChange={aoMudarTabela}
            scroll={{ x: 1250 }}
            onRow={(linha) => {
              if (!agrupar || linha.tipo === "sku") {
                return {
                  onClick: () => setProdutoSelecionado(linha.produto),
                  style: { cursor: "pointer" },
                };
              }
              if (linha.tipo === "categoria") {
                return {
                  onClick: () => alternarCategoria(linha.categoria.key),
                  style: { cursor: "pointer", background: "#fafafa" },
                };
              }
              return {};
            }}
            pagination={
              agrupar
                ? false
                : {
                    current: pagina,
                    pageSize: TAMANHO_DA_PAGINA_DE_SKUS,
                    total: skus.data?.meta?.total ?? 0,
                    showSizeChanger: false,
                  }
            }
            summary={() =>
              linhaDoTotal ? (
                <Table.Summary fixed>
                  <Table.Summary.Row style={{ background: "#fafafa" }}>
                    {colunasDeSku.map((coluna, indice) => (
                      <Table.Summary.Cell
                        key={coluna.key}
                        index={indice}
                        align={coluna.align}>
                        {celulaDoTotal(coluna.key, totais, linhaDoTotal)}
                      </Table.Summary.Cell>
                    ))}
                  </Table.Summary.Row>
                </Table.Summary>
              ) : null
            }
            locale={{
              emptyText: skus.isError
                ? mensagemDeErro(skus.error)
                : buscaAtrasada
                  ? "Nenhum SKU corresponde à busca"
                  : "Nenhum SKU",
            }}
          />
        </Space>
      </Card>
      <Modal
        open={!!produtoSelecionado}
        onCancel={() => setProdutoSelecionado(null)}
        footer={null}
        width={960}
        destroyOnClose
        afterOpenChange={setModalAberto}
        title={graficoDoSku?.titulo || `SKU ${produtoSelecionado || ""}`}>
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          {comparativoDoSkuSelecionado && (
            <TabelaDoComparativo comparativo={comparativoDoSkuSelecionado} />
          )}
          {serieDoSku.isLoading || !modalAberto ? (
            <Flex justify="center" style={{ padding: 48 }}>
              <Spin />
            </Flex>
          ) : serieDoSku.isError ? (
            <Alert
              type="error"
              showIcon
              message={mensagemDeErro(serieDoSku.error)}
            />
          ) : graficoDoSku ? (
            <Space direction="vertical" size={8} style={{ width: "100%" }}>
              <Flex justify="flex-end">
                <ControleDaMediaMovel
                  valor={janelaDaMedia}
                  aoMudar={setJanelaDaMedia}
                />
              </Flex>
              <RangeAnnotatedChart
                series={graficoDoSku.series}
                faixas={graficoDoSku.faixas}
              />
            </Space>
          ) : (
            <Empty description="Sem vendas deste SKU na janela" />
          )}
        </Space>
      </Modal>
    </>
  );
}
